/**
 * HashChip — render a hash, root_id, pubkey, or signature as a
 * fixed-width chip. Truncates the middle ("ed25519:abc1…ef9") so
 * the prefix and suffix stay legible while the chip fits the
 * column it lives in.
 *
 * Click-to-copy is on by default; the chip flashes briefly on copy.
 * The flash is a state-change blink (200 ms — within the spec's
 * motion budget).
 *
 * Always renders in `var(--omega-mono)`, always tabular, always
 * the muted ink. The chip is *information*, not chrome — no border,
 * no rounded corners louder than the rim.
 */

import { useState } from 'react';

const HEAD_CHARS = 8;
const TAIL_CHARS = 6;

function shorten(value: string): string {
  if (value.length <= HEAD_CHARS + TAIL_CHARS + 1) return value;
  return `${value.slice(0, HEAD_CHARS)}…${value.slice(-TAIL_CHARS)}`;
}

export function HashChip({
  value,
  full = false,
  copyable = true,
  ariaLabel,
}: {
  /** The raw value. May or may not include a `prefix:` (`ed25519:`). */
  value: string;
  /** When true, render the full string instead of the middle-truncated form. */
  full?: boolean;
  /** When true (default), clicking copies to clipboard. */
  copyable?: boolean;
  ariaLabel?: string;
}) {
  const [copied, setCopied] = useState(false);
  const display = full ? value : shorten(value);

  const handleClick = async () => {
    if (!copyable) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 800);
    } catch {
      // clipboard unavailable — silently no-op
    }
  };

  return (
    <span
      className="omega-mono"
      role={copyable ? 'button' : undefined}
      tabIndex={copyable ? 0 : undefined}
      aria-label={ariaLabel ?? `hash ${value}`}
      title={value}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (copyable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          void handleClick();
        }
      }}
      style={{
        fontSize: '10pt',
        color: copied ? 'var(--omega-accent)' : 'var(--omega-ink-muted)',
        cursor: copyable ? 'pointer' : 'default',
        transition: 'color 200ms ease-out',
        userSelect: 'all',
      }}
    >
      {display}
    </span>
  );
}
